import { dateHelpers } from '@nocowanie/core/helpers';

import { getParsedListingFilters } from '@app/helpers/listing.helpers';
import { mapRatingToApi } from '@app/helpers/map-api-params.helpers';
import { SearchCriteria, SearchFilters } from '@app/models/search.model';
import { cache, cacheKey, cacheTtl } from '@app/services/cache/cache.helpers';
import { sharedNocApiClient } from '@app/services/noc-api/noc-api-client';

import type { AccommodationCount } from '@nocowanie/noc-api';

const getCachedListingFilterCount = async (
    args: Parameters<typeof sharedNocApiClient.accommodationCount.countAccommodationForListing>,
) => {
    try {
        return await cache(
            cacheKey('listing:listing-filter-count', args),
            () => sharedNocApiClient.accommodationCount.countAccommodationForListing(...args),
            cacheTtl('APP_CACHE_LISTING_FILTER_COUNT', 600), // Cache for 10 minutes
        );
    } catch (error) {
        throw new Error(
            error instanceof Error ? error.message : 'Failed to fetch filter counter',
        );
    }
};

export const getListingFilterCount = async ({
    searchCriteria,
    searchFilters,
    campaignListingId,
}: {
    searchCriteria: Partial<SearchCriteria>;
    searchFilters: SearchFilters;
    campaignListingId?: string;
}): Promise<AccommodationCount | null> => {
    try {
        const {
            checkInDate,
            checkOutDate,
            flexDates,
            poiId,
            geoId,
            locationType,
            guests: { adultsCount, childrenAges, childrenCount } = {},
        } = searchCriteria;

        const {
            rating,
            priceFrom,
            priceTo,
            beachDistance,
            sorting,
            onlineBooking,
            propertyType,
            provision,
            reservationFeatures,
            amenityGroup,
            tags,
        } = getParsedListingFilters({ searchFilters });

        const isCampaignListing = !!campaignListingId;

        const args: Parameters<
            typeof sharedNocApiClient.accommodationCount.countAccommodationForListing
        > = [
            adultsCount,
            undefined,
            amenityGroup,
            onlineBooking === 1 || onlineBooking === 0 ? onlineBooking : undefined,
            Object.assign({}, reservationFeatures) as any,
            Object.assign({}, propertyType) as any,
            childrenCount,
            Object.assign({}, childrenAges),
            checkInDate ? dateHelpers.format(new Date(checkInDate), 'yyyy-MM-dd') : undefined,
            checkOutDate ? dateHelpers.format(new Date(checkOutDate), 'yyyy-MM-dd') : undefined,
            Object.assign({}, provision) as any,
            rating ? mapRatingToApi(rating) : undefined,
            geoId && locationType === 'region' ? parseInt(geoId) : undefined,
            geoId && locationType === 'voivodeship' ? parseInt(geoId) : undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            sorting
                ? ({
                      [sorting.type]: sorting?.order,
                  } as unknown as string[])
                : undefined,
            geoId && locationType === 'city' ? parseInt(geoId) : undefined,
            geoId && locationType === 'country' ? parseInt(geoId) : undefined,
            flexDates,
            isCampaignListing ? 'campaign-listings' : undefined,
            isCampaignListing ? 'accommodationsIDs' : undefined,
            isCampaignListing ? campaignListingId : undefined,
            poiId ? `${poiId}` : undefined,
            priceFrom ?? undefined,
            priceTo ?? undefined,
            beachDistance ?? undefined,
            tags,
        ];

        const response = await getCachedListingFilterCount(args);

        return response || null;
    } catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Failed to fetch filter counter');
    }
};
